"use client";

import { useLocale, useTranslations } from "next-intl";
import React from "react";
import Image from "next/image";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Button } from "./ui/button";
import { Slider } from "./ui/slider";

import { Link } from "@/i18n/routing";
import { cn } from "@/lib/utils";
import { ArrowRight, ChevronRight } from "lucide-react";
import { formatPrice } from "@/lib/helpers/pricing-helper";
import { getCustomerCountry } from "@/lib/helpers/location-helper";
import { useUserStore } from "@/store/store";
import { PricingFreeBadge } from "@/components/pricing-free-badge";

const PricingSection = () => {
  const t = useTranslations("pricing");
  const locale = useLocale();
  const { role } = useUserStore();
  const [sliderValue, setSliderValue] = React.useState<number[]>([50]);
  const [formattedPrices, setFormattedPrices] = React.useState<string[]>([]);
  const [userCountry, setUserCountry] = React.useState<string | null>(null);
  const [hasUserInteracted, setHasUserInteracted] = React.useState(false);
  const pricingHref = `/${role}/pricing`;


  const prices = React.useMemo(() => [
    {
      plan: t("starter.title"),
      price: t("starter.price"),
      clients: t("starter.clients"),
      type: t("starter.type"),
      description: t("starter.description"),
    },
    {
      plan: t("pro.title"),
      price: t("pro.price"),

      type: t("pro.type"),
      description: t("pro.description"),
    },
    {
      plan: t("elite.title"),
      price: t("elite.price"),
      clients: t("elite.clients"),
      type: t("elite.type"),
      description: t("elite.description"),
    },
  ], [t]);

  const badgeMessage = t("badge.message");
  const badgeAlign = locale === "ar" ? "left" : "right";


  const handleSliderChange = (value: number[]) => {
    setSliderValue(value);
    setHasUserInteracted(true);
  };
  
  React.useEffect(() => {
    const fetchCountry = async () => {
      try {
        const res = await getCustomerCountry();
        setUserCountry(res);
  
        localStorage.setItem('country', res);
        localStorage.setItem('country_expiry', String(Date.now() + 1000 * 60 * 60 * 24));
      } catch (err) {
        console.error("Failed to fetch country", err);
      }
    };
  
    const cached = localStorage.getItem('country');
    const expiry = localStorage.getItem('country_expiry');
    const hasExpired = !expiry || Date.now() > Number(expiry);
  
  
    if (cached && !hasExpired) {
      setUserCountry(cached);
    } else {
      fetchCountry();
    }
  }, []);
  

  const hasFormattedPrices = formattedPrices.length > 0;

  React.useEffect(() => {
    if (!hasUserInteracted && hasFormattedPrices) return;
  
    async function updatePrices() {
      const pricesFormatted = await Promise.all(
        prices.map((item, index) =>
          formatPrice(locale, index, item, userCountry, sliderValue[0])
        )
      );
      setFormattedPrices(pricesFormatted);
    }
  
    updatePrices();
  }, [sliderValue, hasUserInteracted, userCountry, locale, prices, hasFormattedPrices]);
  
  return (
    <>
      <section className="pt-16 md:pt-32 px-4 md:px-12 relative bg-gray-100">
        <div className="flex flex-col gap-4 text-center md:flex-row md:text-start justify-between items-center pb-16 md:pb-32">
          <h3 className="text-5xl md:text-7xl xl:text-8xl basis-1/2">
            {t("pricing_section.title")}
          </h3>
          <p className="font-poppins text-xl basis-1/2">
            {t("pricing_section.description")}
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {prices.map((item, index) => (
            <Card
              key={index}
              className="rounded-[3rem] overflow-visible text-primary-foreground bg-gradient-to-b from-primary to-second-primary flex flex-col gap-6 relative"
            >
              {locale === "en" ? (
                <Image
                  src="/free_trial_icon.png"
                  alt="Free trial"
                  width={240}
                  height={240}
                  className="absolute top-4 right-4 w-40 h-40 object-contain drop-shadow-2xl"
                />
              ) : (
                <Image
                  src="/free_trial_icon_ar.png"
                  alt="نسخة تجريبية مجانية"
                  width={240}
                  height={240}
                  className="absolute top-4 left-4 w-40 h-40 object-contain drop-shadow-2xl"
                />
              )}
              <CardHeader className="gap-5 pb-0">
                <span className="font-poppins font-semibold text-xl">
                  {item.plan}
                </span>
                <CardTitle className="text-6xl">
                  {formattedPrices[index] ?? ""}
                </CardTitle>
                <span className="text-xs font-poppins">{item.type}</span>
                {item.clients ? (
                  <span className="text-4xl">{item.clients}</span>
                ) : (
                  <Slider
                    className="h-10"
                    defaultValue={sliderValue}
                    onValueChange={(value) => handleSliderChange(value)}
                    client={t("client")}
                    locale={locale}
                    min={10}
                    max={150}
                    step={10}
                  />
                )}
                <CardDescription className="font-poppins text-primary-foreground h-20 line-clamp-4 text-ellipsis">
                  {item.description}
                </CardDescription>
              </CardHeader>
              <CardContent className="pb-0">
                <Button
                  asChild
                  variant="link"
                  className="text-primary-foreground"
                >
                  <Link href={pricingHref} className="font-bold font-poppins">
                    {t("pricing_section.learn_more")}
                  </Link>
                </Button>
              </CardContent>
              <CardFooter className="flex-1 items-end h-fit">
              <div className="w-full flex flex-col gap-3 items-center">
                <Button
                  className="rounded-full w-full text-3xl pb-6 pt-7"
                  variant="secondary"
                  asChild
                >
                  <Link href="/auth/signup">{t("start")}</Link>
                </Button>
                <span className="text-sm font-semibold text-primary-foreground/90 text-center">
                  {t("no_credit_card")}
                </span>
              </div>
                {/* Temporarily hiding "3 Months Free" messaging on home cards */}
                {/* <PricingFreeBadge
                  message={badgeMessage}
                  align={badgeAlign}
                  variant="subtext"
                  className="mt-4"
                /> */}
              </CardFooter>
            </Card>
          ))}
        </div>
      </section>
      <div className="relative h-32 w-full">
        <Link
          href="/contact-us"
          className={cn(
            "absolute w-fit md:min-w-40 p-2 py-4 top-1/2 -translate-y-1/2 font-poppins flex flex-col bg-black-bg",
            {
              "rounded-tr-lg": locale === "en",
              "rounded-br-lg": locale === "en",
              "rounded-tl-lg": locale === "ar",
              "rounded-bl-lg": locale === "ar",
              "left-0": locale === "en",
              "right-0": locale === "ar",
            }
          )}
        >
          <div className="flex justify-start items-center gap-2 text-white font-semibold">
            <h3 className="">{t("pricing_section.contact_us.title")}</h3>
            <ChevronRight
              className={cn("", {
                "rotate-180": locale === "ar",
              })}
              size={16}
            />
          </div>
          <p className="text-muted-foreground">
            {t("pricing_section.contact_us.description")}
          </p>
        </Link>
      </div>
    </>
  );
};

export default PricingSection;
